export const PAYMENT_TIME_ZONE = "America/Mexico_City";

export function paymentDate(value) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PAYMENT_TIME_ZONE,
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(value));
  const get = (type) => parts.find((part) => part.type === type).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function datePreset(preset, now = new Date()) {
  const today = paymentDate(now);
  if (preset === "all") return { from: "", to: "" };
  if (preset === "today") return { from: today, to: today };
  const first = `${today.slice(0, 7)}-01`;
  if (preset === "last_month") {
    const previous = new Date(`${first}T12:00:00Z`);
    previous.setUTCDate(0);
    const to = previous.toISOString().slice(0, 10);
    return { from: `${to.slice(0, 7)}-01`, to };
  }
  return { from: first, to: today };
}

// Resolve local midnight using the IANA timezone, including Mexico's historical DST.
export function paymentDayStart(day) {
  if (typeof day !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    throw new Error("La fecha no es válida");
  }
  const utc = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(utc) || new Date(utc).toISOString().slice(0, 10) !== day) {
    throw new Error("La fecha no es válida");
  }
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: PAYMENT_TIME_ZONE, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  let result = utc;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(result))
      .filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
    const local = Date.parse(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}Z`);
    const adjustment = utc - local;
    result += adjustment;
    if (!adjustment) return result / 1000;
  }
  throw new Error("No se pudo interpretar la fecha");
}

export function nextPaymentDay(day) {
  paymentDayStart(day);
  const next = new Date(`${day}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

export function formatPaymentTime(value) {
  return new Intl.DateTimeFormat("es-MX", {
    timeZone: PAYMENT_TIME_ZONE, dateStyle: "medium", timeStyle: "short",
  }).format(new Date(value));
}

export function currencyDecimals(currency) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency", currency,
  }).resolvedOptions().maximumFractionDigits;
}

export function formatPaymentMoney(amount, currency) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency", currency, currencyDisplay: "code",
  }).format(amount / 10 ** currencyDecimals(currency));
}

export function paymentStatus(row) {
  const labels = [];
  if (row.disputed) labels.push("Con disputa");
  if (row.refunded >= row.amount && row.amount > 0) labels.push("Reembolsado");
  else if (row.refunded > 0) labels.push("Reembolso parcial");
  else labels.push("Pagado");
  return labels.join(" · ");
}

function searchable(value) {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

export function filterCardPayments(payments, { provider = "", search = "" } = {}) {
  const needle = searchable(search).trim();
  return payments.filter((payment) =>
    (!provider || String(payment.provider_id) === String(provider)) &&
    (!needle || searchable([
      payment.order_id, payment.order_reference, payment.client_name,
      payment.provider_name, payment.charge_id, payment.payment_intent,
    ].join(" ")).includes(needle))
  );
}

export function groupCardPayments(payments) {
  const groups = new Map();
  const seen = new Set();
  for (const payment of payments) {
    if (seen.has(payment.charge_id)) continue;
    seen.add(payment.charge_id);
    // Keep different currencies separate rather than adding unlike amounts.
    const key = payment.order_id
      ? `${payment.order_id}:${payment.currency}` : payment.charge_id;
    if (!groups.has(key)) {
      groups.set(key, { ...payment, key, amount: 0, refunded: 0, disputed: false, charges: [] });
    }
    const row = groups.get(key);
    row.amount += payment.amount;
    row.refunded += payment.refunded;
    row.disputed ||= payment.disputed;
    row.paid_at = row.paid_at > payment.paid_at ? row.paid_at : payment.paid_at;
    row.charges.push(payment);
  }
  return [...groups.values()].sort((a, b) =>
    b.paid_at.localeCompare(a.paid_at) || a.key.localeCompare(b.key));
}

export function summarizeCardPayments(rows) {
  const totals = new Map();
  for (const row of rows) {
    if (!totals.has(row.currency)) totals.set(row.currency, { currency: row.currency, amount: 0, refunded: 0 });
    const total = totals.get(row.currency);
    total.amount += row.amount;
    total.refunded += row.refunded;
  }
  return {
    orders: new Set(rows.map((row) => row.order_id).filter(Boolean)).size,
    totals: [...totals.values()].sort((a, b) => a.currency.localeCompare(b.currency)),
  };
}

function csvCell(value) {
  let text = String(value ?? "");
  // A quoted cell alone does not prevent spreadsheet formula execution.
  if (/^[\s]*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function cardPaymentsCsv(rows) {
  const header = [
    "Orden", "Fecha de pago (Ciudad de México)", "Proveedor", "Cliente",
    "Importe cobrado", "Importe reembolsado", "Moneda", "Estado del pago",
    "Orden cancelada", "Número de cobros", "Referencias Stripe", "Fechas de los cobros (Ciudad de México)",
  ];
  const lines = rows.map((row) => {
    const decimals = currencyDecimals(row.currency);
    const amount = (value) => (value / 10 ** decimals).toFixed(decimals);
    return [
      row.order_id || row.order_reference || "", formatPaymentTime(row.paid_at),
      row.provider_name, row.client_name, amount(row.amount), amount(row.refunded),
      row.currency.toUpperCase(), paymentStatus(row), row.cancelled ? "Sí" : "No",
      row.charges.length, row.charges.map((charge) => charge.charge_id).join("; "),
      row.charges.map((charge) => formatPaymentTime(charge.paid_at)).join("; "),
    ];
  });
  return "\uFEFF" + [header, ...lines].map((line) => line.map(csvCell).join(",")).join("\r\n");
}
